import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import useApi from "../../../CustomHooks/useApi";
import Popup from "../../../ResuableCompoment/Popup";
import { fetchAepsBalance, fetchSettlementBalance, fetchWalletBalance } from "../../../Store/Slices/WalletSlice";

const AepsFundTransfer = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { selectedBeneficiary = [], amount = 0 } = location.state || {};
    const beneficiary = selectedBeneficiary[0];
    const { lat, lng, status } = useSelector((s) => s.location);
    const latitude = lat;
    const longitude = lng;

    const remitterMobile = useSelector(
        (state) => state.auth.remitterData?.remitter_phone
    );

    const username = useSelector(
        (state) => state.auth.employeeDetails?.username
    );

    /* ---------------- GEO LOCATION (SAME AS AEPS SETTLEMENT) ---------------- */
    const [mpin, setMpin] = useState("");
    const mpinRefs = React.useRef([]);


    /* ---------------- EMAIL INPUT ---------------- */

    /* ---------------- FEE STATE ---------------- */
    const [fee, setFee] = useState(0);
    const [txnAmount, setTxnAmount] = useState(0); // ✅ from fee API
    const [totalAmount, setTotalAmount] = useState(0);
    const [loadingFee, setLoadingFee] = useState(false);

    /* ---------------- UI STATE ---------------- */
    const [isLoading, setIsLoading] = useState(false);
    const [popupMessage, setPopupMessage] = useState("");
    const [popupType, setPopupType] = useState("");
    const [showPopup, setShowPopup] = useState(false);

    const { callApi: calculateFee } = useApi("/dmt/fee/calculate/aeps/settle", "POST");
    const { callApi: payout } = useApi("/payout/single/payout", "POST");

    /* ---------------- FEE CALCULATION ---------------- */
    useEffect(() => {
        const calculateCharges = async () => {
            if (!amount || Number(amount) <= 0) return;

            setLoadingFee(true);

            try {
                const payload = {
                    paymentMode: "IMPS",
                    provider: "SETTLEMENT",
                    sub_provider: "NIFI",
                    amount: Number(amount),
                    commercial_type: "TRANSACTION_FEE",
                    mid: username
                };

                const res = await calculateFee(payload);

                const calculatedFee = Number(res?.fee || 0);
                const calculatedTxnAmount =
                    Number(res?.txn_amount) || Number(amount) + calculatedFee;

                setFee(calculatedFee);
                setTxnAmount(calculatedTxnAmount); // ✅ IMPORTANT
                setTotalAmount(calculatedTxnAmount);
            } catch (err) {
                setFee(0);
                setTxnAmount(Number(amount));
                setTotalAmount(Number(amount));
            } finally {
                setLoadingFee(false);
            }
        };

        calculateCharges();
    }, [amount]);

    const isValidEmail = (email) => {
        if (!email) return false;
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    };

    const handleMpinChange = (index, value) => {
        if (!/^[0-9]?$/.test(value)) return;

        const newMpin = mpin.split("");
        newMpin[index] = value;
        const updated = newMpin.join("");
        setMpin(updated);

        // Auto focus next
        if (value && index < 5) {
            mpinRefs.current[index + 1].focus();
        }
    };

    const handleMpinBackspace = (index, e) => {
        if (e.key === "Backspace" && !mpin[index] && index > 0) {
            mpinRefs.current[index - 1].focus();
        }
    };


    /* ---------------- PAYOUT ---------------- */
    const handleTransferAmount = async () => {
        if (!latitude || !longitude) {
            setPopupMessage("Please enable location access to proceed.");
            setPopupType("error");
            setShowPopup(true);
            return;
        }
        if (!mpin || mpin.length !== 6) {
            setPopupMessage("Enter MPIN required");
            setPopupType("error");
            setShowPopup(true);
            return;
        }


        setIsLoading(true);

        try {
            const payload = {
                mid: username,
                beneficiary_account_number: beneficiary.accountNo,
                beneficiary_ifsc_code: beneficiary.ifscCode,
                beneficiary_name: beneficiary.beneficiaryName,
                beneficiary_mobile: remitterMobile,
                beneficiary_email: "info@bingopays.in",
                beneficiary_bank_name: beneficiary.bankName,
                paymentMode: "IMPS",
                netAmount: Number(amount),
                txnAmount: txnAmount,
                lat: latitude,
                lng: longitude,
                customer_mobile: remitterMobile,
                mpin: mpin,
            };

            const res = await payout(payload);

            const status = res?.status || "FAILED";
            const message = res?.message || "Transaction failed";
            const utr = res?.utr || "NA";

            if (status === "SUCCESS") {
                setPopupMessage(`✅ ${message}\nUTR: ${utr}`);
                setPopupType("success");
                setShowPopup(true);
                return;
            }

            if (status === "PENDING") {
                setPopupMessage(`⏳ ${message}`);
                setPopupType("warning"); // if Popup supports warning
                setShowPopup(true);
                return;
            }
            setPopupMessage(`${message}`);
            setPopupType("error");
            setShowPopup(true);
        } catch (err) {
            setPopupMessage(err.message || "Payout failed");
            setPopupType("error");
            setShowPopup(true);
        } finally {
            setIsLoading(false);
            dispatch(fetchWalletBalance());
            dispatch(fetchAepsBalance());
            dispatch(fetchSettlementBalance());
        }
    };

    if (!beneficiary) {
        return (
            <div className="p-6 text-center text-red-600">
                Invalid access. Please start from AEPS Remitter.
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto mt-10 bg-white rounded-3xl shadow-lg p-8">
            <h2 className="text-2xl font-semibold mb-6 text-slate-800">
                AEPS Fund Transfer
            </h2>

            {/* BENEFICIARY */}
            <div className="mb-6 border rounded-xl p-4 bg-slate-50">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">
                    Beneficiary Details
                </h3>
                <div className="text-sm space-y-1">
                    <div><b>Name:</b> {beneficiary.beneficiaryName}</div>
                    <div><b>Account No:</b> {beneficiary.accountNo}</div>
                    <div><b>IFSC:</b> {beneficiary.ifscCode}</div>
                    <div><b>Bank:</b> {beneficiary.bankName}</div>
                </div>
            </div>


            {/* BREAKDOWN */}
            <div className="border rounded-xl p-4 bg-slate-50">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">
                    Amount Breakdown
                </h3>

                {loadingFee ? (
                    <p className="text-sm text-slate-500">Calculating charges…</p>
                ) : (
                    <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                            <span>Transfer Amount</span>
                            <span>₹ {Number(amount).toFixed(2)}</span>
                        </div>

                        <div className="flex justify-between">
                            <span>Charges</span>
                            <span>₹ {fee.toFixed(2)}</span>
                        </div>

                        <div className="flex justify-between font-semibold text-base pt-2 border-t">
                            <span>Total Debit</span>
                            <span>₹ {totalAmount.toFixed(2)}</span>
                        </div>
                    </div>
                )}
            </div>
            {/* MPIN INPUT */}
            <div className="mt-6">
                <label className="text-sm font-medium text-slate-700">
                    Enter MPIN <span className="text-red-500">*</span>
                </label>

                <div className="flex gap-3 mt-2">
                    {[...Array(6)].map((_, i) => (
                        <input
                            key={i}
                            ref={(el) => (mpinRefs.current[i] = el)}
                            type="password"
                            maxLength={1}
                            value={mpin[i] || ""}
                            onChange={(e) => handleMpinChange(i, e.target.value)}
                            onKeyDown={(e) => handleMpinBackspace(i, e)}
                            className="w-12 h-12 text-center text-xl border-2 border-slate-800 rounded-lg focus:border-indigo-600 outline-none"
                        />
                    ))}
                </div>
            </div>


            {/* ACTION */}
            <div className="flex justify-end mt-8">
                <button
                    onClick={handleTransferAmount}
                    disabled={
                        isLoading ||
                        loadingFee
                    }
                    className={`px-10 py-4 rounded-2xl font-semibold text-white
            ${isLoading || loadingFee
                            ? "bg-slate-400 cursor-not-allowed"
                            : "bg-gradient-to-r from-indigo-600 to-cyan-500 hover:shadow-xl"
                        }`}
                >
                    {isLoading ? "Processing..." : "Transfer Amount"}
                </button>
            </div>

            {showPopup && (
                <Popup
                    message={popupMessage}
                    type={popupType}
                    navigateTo="/transactionhistory"
                    onClose={() => {
                        setShowPopup(false);
                        navigate("/transactionhistory");
                    }}
                    showchildren={false}
                />
            )}
        </div>
    );
};

export default AepsFundTransfer;
